"use client";

import React from "react";
import { ShieldCheck, Phone, ArrowRight, CircleCheck as CheckCircle2, Building2, House as Home, Hammer, Compass } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const services = [{
  title: "Commercial",
  description: "Multi-storey access, refurbishments, and planned site programs.",
  icon: Building2
}, {
  title: "Residential",
  description: "Extensions, private builds, and safe homeowner access.",
  icon: Home
}, {
  title: "Roofing",
  description: "Independent scaffolding, edge protection, and temporary roofs.",
  icon: Hammer
}, {
  title: "Specialist Access",
  description: "Bespoke structures, confined spaces, and complex cantilevers.",
  icon: Compass
}];
const accreditations = ["NASC Member", "CHAS Accredited", "20+ Years Experience"];
const wp_query_params = {
  post_type: "site_photo",
  per_page: 100,
  status: ["publish"]
};
const wp_query = new WP_Query(wp_query_params);
function BeaconsfieldHeroImage() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="h-full w-full bg-muted animate-pulse" />;
  }
  const matchedPost = posts?.find(post => {
    const customFields = post?.customFields || {};
    const acf = (post as any)?.acf || {};
    return customFields.photo_name === "Beaconsfield Scaffolding Hero Image" || acf.photo_name === "Beaconsfield Scaffolding Hero Image";
  });
  const getImageUrl = (val: any) => {
    if (typeof val === "string") return val;
    if (val && typeof val === "object" && typeof val.url === "string") return val.url;
    return undefined;
  };
  const dynamicSrc = getImageUrl(matchedPost?.customFields?.image) || getImageUrl((matchedPost as any)?.acf?.image) || matchedPost?.featuredImage;
  const dynamicAlt = matchedPost?.customFields?.alt_text || (matchedPost as any)?.acf?.alt_text || matchedPost?.title;
  const isDynamic = Boolean(dynamicSrc);
  const src = dynamicSrc || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_beaconsfield_hero.png";
  const alt = dynamicAlt || "Scaffolding structure installation in Beaconsfield";
  return <img src={src} data-wvc-srcset={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_beaconsfield_hero.png-wvc-srcset"} data-wvc-sizes={isDynamic ? "" : "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/scaffolding_beaconsfield_hero.png-wvc-sizes"} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />;
}
export default function Beaconsfield() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="herobeaconsfield" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-20 lg:py-28">
      {/* Background decoration & overlay image */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-25">
        <PostsProvider wp_query={{
        post_type: "site_photo",
        per_page: 100,
        status: ["publish"]
      }} className="h-full w-full">
          <BeaconsfieldHeroImage />
        </PostsProvider>
        <div className="absolute inset-0 bg-gradient-to-r from-secondary via-secondary/90 to-secondary/80" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/20 bg-secondary/80 px-3 py-1 text-xs font-medium text-secondary-foreground backdrop-blur-xs">
                <ShieldCheck className="size-3.5 text-primary" />
                <span>NASC &amp; CHAS Accredited</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-default tracking-tight text-secondary-foreground leading-tight">
                Scaffolding in Beaconsfield
              </h1>

              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl font-sans leading-relaxed">
                Safety-led scaffolding contractor delivering dependable access for commercial developments, residential properties, roofing contractors, and specialist structures across Beaconsfield and the wider Home Counties.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium text-base h-12 px-6">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a Quote</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border/30 bg-secondary/60 text-secondary-foreground hover:bg-secondary hover:text-secondary-foreground hover:border-border/50 text-base h-12 px-6">
                <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                  <Phone className="size-4 text-primary" />
                  <span className="text-secondary-foreground">Call 01344 929900</span>
                </a>
              </Button>
            </div>

            {/* Quick trust proofs */}
            <div className="pt-4 border-t border-border/15 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {accreditations.map((item, i) => <div data-index={i} key={item} className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span className="font-medium text-secondary-foreground">{item}</span>
                </div>)}
            </div>
          </div>

          {/* Service Matrix Card */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-border/20 bg-secondary/90 backdrop-blur-md p-6 sm:p-8 shadow-xl">
              <div className="flex items-center justify-between pb-6 border-b border-border/20 mb-6">
                <div>
                  <h2 className="text-xl font-bold font-default text-secondary-foreground">
                    Access Services
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Engineered to NASC standards with full safety audits
                  </p>
                </div>
                <Badge variant="secondary" className="bg-primary/15 text-primary border-primary/20">
                  Beaconsfield &amp; Bucks
                </Badge>
              </div>

              <div className="space-y-4">
                {services.map((service, i) => {
                const Icon = service.icon;
                return <motion.div data-index={i} key={service.title} initial={shouldReduceMotion ? {
                  opacity: 1
                } : {
                  opacity: 0,
                  y: 10
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  delay: i * 0.08
                }} className="group flex items-start gap-4 p-3.5 rounded-xl border border-border/10 bg-secondary/60 hover:bg-secondary hover:border-border/30 transition-[color,background-color,border-color,box-shadow]">
                      <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                        <Icon className="size-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-secondary-foreground font-default">
                          {service.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                          {service.description}
                        </p>
                      </div>
                    </motion.div>;
              })}
              </div>

              <div className="mt-6 pt-5 border-t border-border/20 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
                <span>Direct site visits &amp; prompt estimates</span>
                <Link to="/AreasWeCoverPage" className="text-primary hover:underline font-medium inline-flex items-center gap-1">
                  <span>View all coverage areas</span>
                  <ArrowRight className="size-3" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}